import { ReactNode, useState } from 'react';
import { Menu } from 'lucide-react';
import ModernSidebar from '../layout/ModernSidebar';
import { TopbarBackgroundStyle } from '../layout/EnhancedModernTopbar';
import PageHeader from '../layout/PageHeader';
import { BreadcrumbItem } from '../shared/Breadcrumb';
import { useAuth } from '../../hooks/useAuth';
import useMediaQuery from '../../hooks/useMediaQuery';
import NetworkStatusBanner from '../shared/NetworkStatusBanner';

interface SearchField {
  id: string;
  label: string;
  placeholder: string;
}

interface MainContainerProps {
  children: ReactNode;
  title?: string;
  showTopbar?: boolean;
  // Background styling
  topbarBackgroundStyle?: TopbarBackgroundStyle;
  // Breadcrumb functionality
  breadcrumbItems?: BreadcrumbItem[];
  showBackButton?: boolean;
  onBack?: () => void;
  // Search functionality
  enableSearch?: boolean;
  searchFields?: SearchField[];
  onSearch?: (query: string, field: string) => void;
  onSearchFieldChange?: (field: string) => void;
  initialSearchQuery?: string;
  initialSearchField?: string;
  enableScan?: boolean;
  // Enable user logout section in the topbar
  enableUserLogoutSection?: boolean;
  // Action buttons
  primaryAction?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
  };
  customHeader?: ReactNode;
  showSidebar?: boolean;
  enableWideScreen?: boolean;
  // Opt-in "app-shell" layout: on lg+ the page itself does NOT scroll — the content fills the
  // viewport and an inner region (e.g. a data-table body) owns the scroll, so filters/headers/
  // pager stay pinned. Mobile keeps normal full-page scroll. Default false (every other page
  // is unchanged).
  fitToViewport?: boolean;
}

/**
 * Every page mounts its own MainContainer, so navigating unmounts the shell and
 * remounts it. Keeping the collapse choice in component state meant it reverted
 * to the width default on every route change; it lives in storage instead.
 */
const SIDEBAR_COLLAPSED_KEY = 'sidebar:collapsed';

const readStoredCollapse = (): boolean | null => {
  try {
    const raw = window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return raw === null ? null : raw === '1';
  } catch {
    // Private mode / storage disabled — fall back to the width default.
    return null;
  }
};

const MainContainer: React.FC<MainContainerProps> = ({ 
  children, 
  title,
  showTopbar = true,
  showSidebar = true,
  showBackButton = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- inert: the chrome is neutral
  topbarBackgroundStyle = TopbarBackgroundStyle.DEFAULT,
  breadcrumbItems = [],
  onBack,
  enableSearch = false,
  searchFields = [],
  onSearch,
  onSearchFieldChange,
  initialSearchQuery,
  initialSearchField,
  enableScan = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- inert: logout lives in the sidebar
  enableUserLogoutSection = true,
  primaryAction,
  customHeader,
  enableWideScreen = true,
  fitToViewport = false,
}) => {
  const { impersonating } = useAuth();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  // A remembered choice wins; otherwise expanded from xl up, icon rail below it —
  // see the breakpoint table in the design canvas.
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return true;
    return readStoredCollapse() ?? window.innerWidth < 1280;
  });

  const toggleSidebarCollapse = () => setIsSidebarCollapsed(prev => {
    const next = !prev;
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
    } catch {
      // Not being able to remember it is not a reason to refuse the toggle.
    }
    return next;
  });
  const wScreenStyle = enableWideScreen ? 'w-screen' : '';
  return (
    <div className={`flex h-dvh ${wScreenStyle} overflow-hidden bg-[#E4E8EC]`}>
      {/* Mobile Sidebar Overlay */}
      {showSidebar && isMobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}
      
      {/* One sidebar: static rail or panel from md up, overlay drawer below it.
          It used to be rendered twice — a desktop copy and a mobile copy — which
          mounted the whole nav, and its state, twice on every route. */}
      {showSidebar && (
        <ModernSidebar
          isOpen={isDesktop || isMobileSidebarOpen}
          isCollapsed={isDesktop ? isSidebarCollapsed : false}
          onToggleCollapse={isDesktop ? toggleSidebarCollapse : () => setIsMobileSidebarOpen(false)}
          canCollapse={isDesktop}
          onNavigate={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Main content area — one inset panel on the app ground, edge to edge on phones */}
      <div className="relative flex-1 flex flex-col overflow-hidden min-w-0 bg-white
                      md:m-2.5 md:ml-0 md:rounded-[10px] md:border md:border-[#D5DCE2]
                      md:shadow-[0_1px_2px_rgba(14,20,25,0.05),0_10px_28px_-16px_rgba(14,20,25,0.22)]">
        {/* The drawer toggle normally rides in the page header; pages that hide the
            header still need a way to open it. */}
        {showSidebar && !showTopbar && (
          <button
            type="button"
            onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
            className="absolute left-2 top-2 z-30 flex h-10 w-10 items-center justify-center text-[#0E1419] md:hidden"
            aria-label="เปิดเมนู"
          >
            <Menu className="h-5 w-5" />
          </button>
        )}

        <NetworkStatusBanner />
        <main
          // The only thing that ever covers the bottom of the page is the
          // impersonation banner, which renders null unless you are impersonating —
          // so the space is reserved then, not on every page forever.
          className={`flex-1 overflow-y-auto bg-[#F5F7F9] ${impersonating ? 'pb-16' : ''} ${
            fitToViewport ? 'lg:overflow-hidden' : ''
          }`}
        >
          <div className={`min-h-full ${fitToViewport ? 'lg:flex lg:h-full lg:min-h-0 lg:flex-col' : ''}`}>
            {showTopbar && (
              <PageHeader
                title={title}
                breadcrumbItems={breadcrumbItems}
                showBackButton={showBackButton}
                onBack={onBack}
                enableSearch={enableSearch}
                searchFields={searchFields}
                onSearch={onSearch}
                onSearchFieldChange={onSearchFieldChange}
                initialSearchQuery={initialSearchQuery}
                initialSearchField={initialSearchField}
                enableScan={enableScan}
                onMenuClick={showSidebar ? () => setIsMobileSidebarOpen(!isMobileSidebarOpen) : undefined}
                primaryAction={primaryAction}
              >
                {customHeader}
              </PageHeader>
            )}
            <div className={fitToViewport ? 'lg:min-h-0 lg:flex-1' : ''}>{children}</div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MainContainer;
